import type { realtimeV1 } from '@udm/contracts';
import { loadStatic, removeStatic } from './deviceStatic';
import type { EntityStore } from './entityStore';
import type { DeviceLive, DeviceStatic, Presence, Severity } from './types';

/**
 * Routes realtime batch events to the owning store. It has NO access to the REST cache and never triggers
 * refetches: a device event patches exactly that device (PROJECT_SPEC §47, Rule 13).
 */
export interface RouterTargets {
  devices: EntityStore<DeviceLive>;
  onCommandStatus?: (commandId: string, payload: Record<string, unknown>) => void;
  loadStatic?: (items: DeviceStatic[]) => void;
  removeStatic?: (id: string) => void;
  onUnhandled?: (event: realtimeV1.BatchEvent) => void;
}

export function createEventRouter(targets: RouterTargets) {
  return (events: readonly realtimeV1.BatchEvent[]): void => {
    for (const e of events) {
      switch (e.k) {
        case 'ds':
          targets.devices.patch(e.id, { ...(e.p as Partial<DeviceLive>), sv: e.sv ?? 0 });
          break;
        case 'pr':
          targets.devices.patch(e.id, {
            sv: e.sv ?? 0,
            presence: e.p.presence as Presence,
            ...(typeof e.p.lastSeenAt === 'string' ? { lastSeenAt: e.p.lastSeenAt } : {}),
            ...('presenceReason' in e.p ? { presenceReason: e.p.presenceReason as string | null } : {}),
            ...('presenceChangedAt' in e.p
              ? { presenceChangedAt: e.p.presenceChangedAt as string | null }
              : {}),
            ...('connectedSince' in e.p ? { connectedSince: e.p.connectedSince as string | null } : {}),
          });
          break;
        case 'reg': {
          // Registry change (adopted / moved / renamed): static data travels in the event — no refetch.
          if (e.p.removed === true) {
            // Left the registry (back to Discovered): drop exactly this device.
            (targets.removeStatic ?? removeStatic)(e.id);
            targets.devices.remove(e.id);
            break;
          }
          const s = e.p.static as DeviceStatic | undefined;
          if (s) (targets.loadStatic ?? loadStatic)([s]);
          const live = e.p.live as Partial<DeviceLive> | undefined;
          if (live) targets.devices.patch(e.id, { ...live, sv: e.sv ?? 0 });
          break;
        }
        case 'al':
          targets.devices.patch(e.id, { sv: e.sv ?? 0, alarm: (e.p.severity as Severity | null) ?? null });
          break;
        case 'cmd':
          targets.onCommandStatus?.(e.id, e.p);
          break;
        default:
          targets.onUnhandled?.(e);
      }
    }
  };
}
